import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus, ShieldAlert, Clock, KeyRound, Copy, Check, Ban, Stethoscope,
  Building2, AlertTriangle, RefreshCw,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import { CAPACIDADES_DEL_MENU } from "../components/AppLayout";

// ═══════════════════════════════════════════════════════════════════════════════
// COLABORADORES (COL.10) — la pantalla que faltaba para que el módulo sea usable.
//
// 🔴 AQUÍ NO SE ESCRIBE LA LISTA DE LAS 9 CAPACIDADES. El catálogo (código, nombre,
//    descripción y orden) viaja en la respuesta de `GET /api/colaboradores` y se pinta
//    tal cual llega. Una segunda lista en JavaScript es la forma exacta de DT.235,
//    DT.247 y DT.248 -- cuatro apariciones ya del mismo defecto: el dato correcto
//    escrito dos veces, que se desincroniza justo cuando nadie está mirando.
//
// 🔴 NUNCA SE PINTA UN NOMBRE DE ROL. "Recepción", "Médico" y "Contador" son botones
//    que marcan casillas y nada más: no se guardan, no viajan al backend y no describen
//    a nadie. En cuanto alguien toca una casilla suelta, un rol guardado pasaría a
//    describir MAL lo que el sistema hace, y describirlo mal es peor que no describirlo.
//
// 🔴 ESCONDER UN BOTÓN NO ES UN PERMISO. El servidor rechaza con 403 en los 117
//    endpoints, en tres capas. Lo que esta pantalla hace es no ofrecer puertas cerradas.
// ═══════════════════════════════════════════════════════════════════════════════

// Los tres atajos. Marcan casillas de salida y NADA MÁS: no se guardan en ninguna
// parte, no viajan en el cuerpo de la petición, y después de pulsarlos las 9 casillas
// siguen visibles y modificables. Quien quiera una recepción que además vea reportes,
// pulsa Recepción y marca `reportes`.
const ATAJOS = [
  {
    id: "recepcion",
    etiqueta: "Recepción",
    caps: ["agenda", "pacientes", "whatsapp", "cobros", "cobros_anular"],
    pista: "Agenda, pacientes, bandeja y cobros (incluida la anulación).",
  },
  {
    id: "medico",
    etiqueta: "Médico",
    caps: ["agenda", "pacientes", "cobros", "cobros_anular"],
    pista: "Lo de un consultorio propio. Hay que vincularlo a un médico del catálogo.",
    exigeMedico: true,
  },
  {
    id: "contador",
    etiqueta: "Contador",
    caps: ["reportes"],
    pista: "Sólo los reportes. Ni agenda, ni pacientes, ni bandeja.",
  },
];

/** 🔴 El TEXTO del error sale del backend, no se reescribe aquí: duplicarlo crea dos
 *  fuentes de verdad que se desincronizan (el caso `DOCTOR_PLAN_LIMITS`). */
const mensajeDe = (e) =>
  formatApiError(e?.response?.data?.detail ?? e?.response?.data) ||
  "No se pudo completar la operación.";

const fechaLarga = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleString("es-MX", {
      day: "numeric", month: "long", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

/** Los tres estados de la contraseña temporal, que NO son dos.
 *  `pendiente` espera al colaborador; `caducada` espera al admin. Pintarlos igual
 *  deja al admin sin saber cuál de los dos le toca a él. */
function estadoTemporal(c) {
  // 🔴 COL.11 -- UNA PERTENENCIA REVOCADA NO TIENE CONTRASEÑA QUE ESPERAR.
  //    Revocar `user_clinics` cierra la puerta desde COL.9 (`get_current_user` rechaza con
  //    PERTENENCIA_REVOCADA), pero `password_temporal` y su `exp` se quedan en la fila tal
  //    como estaban -- no se limpian, y es correcto que no se limpien: son el registro de
  //    lo que pasó. Lo que estaba mal era pintarlos igual que los de una pertenencia viva:
  //    la fila decía "Contraseña temporal sin usar — caduca el 19 de septiembre" sobre una
  //    cuenta que YA NO PUEDE ENTRAR con ninguna contraseña, y con una fecha futura encima.
  //    Medido el 16 Sep 2026 contra producción: 5 filas reales en ese estado.
  //    ⚠️ Y esto es lo que Jesús vio en `col10-caducada`: la cuenta se llama así porque se
  //       creó para probar la caducidad, pero su `exp` en la base es FUTURO (19-sep) --
  //       la fecha nunca estuvo mal ni se regeneraba al listar; lo que sobraba era la marca.
  if (c.revocado) return null;
  if (!c.password_temporal) return null;
  const exp = c.password_temporal_exp ? new Date(c.password_temporal_exp) : null;
  if (exp && exp.getTime() <= Date.now()) return "caducada";
  return "pendiente";
}

export default function Colaboradores() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [cargando, setCargando] = useState(true);

  // El catálogo de médicos es una llamada APARTE, y su fallo se cuenta aparte.
  // ⚠️ `GET /api/doctors` exige la capacidad `agenda`, no `colaboradores`: alguien que
  //    sólo administre colaboradores recibe 403 aquí. Se dice con palabras en vez de
  //    dejar un selector vacío que parecería "esta clínica no tiene médicos".
  const [doctores, setDoctores] = useState(null);
  const [doctoresError, setDoctoresError] = useState(null);

  const [abrirAlta, setAbrirAlta] = useState(false);
  const [temporalNueva, setTemporalNueva] = useState(null); // {email, password, exp, aviso}
  const [confirmarRevocar, setConfirmarRevocar] = useState(null);
  const [editando, setEditando] = useState(null); // user_id

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data: d } = await api.get("/colaboradores");
      setData(d);
      setLoadError(null);
    } catch (e) {
      // 🔴 NO se hace `.catch(() => setData({colaboradores: []}))`. Ese patrón existe en
      //    este repo y colapsa "falló la carga" con "no hay nadie": el resultado es una
      //    pantalla vacía que se lee como un hecho. Son dos cosas distintas y se pintan
      //    distinto.
      setLoadError(mensajeDe(e));
      setData(null);
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    api.get("/doctors")
      .then((r) => { setDoctores(r.data.items || r.data || []); setDoctoresError(null); })
      .catch((e) => { setDoctores(null); setDoctoresError(mensajeDe(e)); });
  }, []);

  // `useMemo` y no una expresión suelta: sin él, `|| []` crea un arreglo nuevo en cada
  // render y los dos `useMemo` de abajo se recalcularían siempre.
  const catalogo = useMemo(() => data?.capacidades_catalogo || [], [data]);
  const nombreCap = useMemo(() => {
    const m = {};
    catalogo.forEach((c) => { m[c.code] = c.nombre; });
    return m;
  }, [catalogo]);

  const nombreDoctor = useMemo(() => {
    const m = {};
    (doctores || []).forEach((d) => { m[d.id] = d.name; });
    return m;
  }, [doctores]);

  // 🔴 EL CONTROL. Esta pantalla es el único sitio donde conviven el catálogo REAL del
  //    backend y los DOS puntos del frontend que nombran capacidades por su código: el
  //    mapa pantalla -> capacidad del menú (`AppLayout.js`) y las marcas de salida de los
  //    tres atajos de aquí abajo. Ninguno de los dos es una copia del catálogo -- no
  //    llevan nombre, ni descripción, ni orden -- pero los dos envejecen igual de mal:
  //    el día que un código cambie, el menú escondería entradas perfectamente usables y
  //    un atajo marcaría casillas que no existen, las dos cosas EN SILENCIO. Un mapa que
  //    nadie verifica es DT.235 / DT.247 / DT.248 por cuarta vez; esto es lo que hace que
  //    sí lo verifique alguien.
  const codigosDesconocidos = useMemo(() => {
    if (!catalogo.length) return [];
    const reales = new Set(catalogo.map((c) => c.code));
    const usados = new Set([...CAPACIDADES_DEL_MENU, ...ATAJOS.flatMap((a) => a.caps)]);
    return [...usados].filter((c) => !reales.has(c)).sort();
  }, [catalogo]);

  const revocar = async (c) => {
    try {
      await api.post(`/colaboradores/${c.user_id}/revocar`);
      toast.success(`${c.nombre} ya no tiene acceso a esta clínica.`);
      setConfirmarRevocar(null);
      cargar();
    } catch (e) {
      toast.error(mensajeDe(e));
    }
  };

  const regenerar = async (c) => {
    try {
      const { data: r } = await api.post(`/colaboradores/${c.user_id}/password-temporal`);
      setTemporalNueva({
        email: c.email, password: r.password_temporal,
        exp: r.password_temporal_exp, aviso: r.aviso,
      });
      cargar();
    } catch (e) {
      toast.error(mensajeDe(e));
    }
  };

  const vivos = (data?.colaboradores || []).filter((c) => !c.revocado);
  const revocados = (data?.colaboradores || []).filter((c) => c.revocado);

  return (
    <div className="space-y-7">
      <PageHeader
        title="Colaboradores"
        subtitle="Quién entra a esta clínica y qué puede hacer dentro."
        actions={
          <button
            onClick={() => setAbrirAlta(true)}
            disabled={!data || !data.puede_agregar}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40"
            data-testid="btn-nuevo-colaborador"
          >
            <UserPlus className="w-4 h-4" /> Dar de alta
          </button>
        }
      />

      {data && data.limite_plan != null && (
        <p className="text-sm text-slate-600 pt-0.5">
          {vivos.length} de {data.limite_plan} colaboradores con acceso.
          {!data.puede_agregar && " Tu plan no admite más: revoca a alguien o cambia de plan."}
        </p>
      )}

      {codigosDesconocidos.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="aviso-codigos-viejos">
          <p className="font-semibold flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> El panel nombra permisos que el servidor
            ya no reconoce
          </p>
          <p className="mt-1">
            {codigosDesconocidos.join(", ")}. Las entradas de menú que dependan de ellos
            están ocultas aunque las pantallas quizá funcionen, y los atajos de alta que
            los marquen no marcarán nada. Hay que actualizar el mapa de{" "}
            <code>AppLayout.js</code> y los atajos de esta pantalla.
          </p>
        </div>
      )}

      {cargando && <p className="text-slate-500 text-sm">Cargando…</p>}

      {loadError && (
        <div className="rounded-xl border border-rose-300 bg-rose-50 p-4" data-testid="colab-load-error">
          <p className="font-semibold text-rose-900 flex items-center gap-2">
            <ShieldAlert className="w-4 h-4" /> No se pudo cargar la lista
          </p>
          <p className="text-sm text-rose-800 mt-1">{loadError}</p>
          <button onClick={cargar} className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-rose-900 underline">
            <RefreshCw className="w-4 h-4" /> Reintentar
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-4 pt-1" data-testid="colab-lista">
          {vivos.map((c) => (
            <Fila
              key={c.user_id}
              c={c}
              esYo={c.user_id === user?.id}
              nombreCap={nombreCap}
              nombreDoctor={nombreDoctor}
              doctoresError={doctoresError}
              onRevocar={() => setConfirmarRevocar(c)}
              onRegenerar={() => regenerar(c)}
              onEditar={() => setEditando(c.user_id)}
            />
          ))}
          {revocados.length > 0 && (
            <details className="pt-2">
              <summary className="text-sm text-slate-500 cursor-pointer">
                {revocados.length} con el acceso revocado
              </summary>
              <div className="space-y-3 mt-3">
                {revocados.map((c) => (
                  <Fila key={c.user_id} c={c} esYo={false} nombreCap={nombreCap}
                        nombreDoctor={nombreDoctor} doctoresError={doctoresError} />
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {abrirAlta && (
        <AltaModal
          catalogo={catalogo}
          doctores={doctores}
          doctoresError={doctoresError}
          onClose={() => setAbrirAlta(false)}
          onCreado={(r) => {
            setAbrirAlta(false);
            setTemporalNueva(r);
            cargar();
          }}
        />
      )}

      {temporalNueva && (
        <TemporalModal datos={temporalNueva} onClose={() => setTemporalNueva(null)} />
      )}

      {confirmarRevocar && (
        <Modal
          open
          onClose={() => setConfirmarRevocar(null)}
          title="Revocar el acceso"
          testid="modal-revocar"
          footer={
            <>
              <button onClick={() => setConfirmarRevocar(null)} className="px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold">
                Cancelar
              </button>
              <button onClick={() => revocar(confirmarRevocar)} className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold" data-testid="btn-confirmar-revocar">
                Revocar el acceso
              </button>
            </>
          }
        >
          {/* La confirmación explica lo que implica, no pregunta "¿estás seguro?". */}
          <p className="text-sm text-slate-700">
            <strong>{confirmarRevocar.nombre}</strong> ({confirmarRevocar.email}) dejará de
            poder entrar a esta clínica de inmediato, aunque tenga la sesión abierta.
          </p>
          <ul className="text-sm text-slate-700 mt-3 space-y-1 list-disc pl-5">
            <li>Sus permisos quedan revocados con ella: no vuelve a entrar ni con la contraseña correcta.</li>
            <li><strong>Nada se borra.</strong> Lo que hizo —cobros, gastos, citas— se queda con su nombre, y queda registrado quién y cuándo le revocó el acceso.</li>
            <li>Si más adelante tiene que volver, se le da de alta otra vez con una contraseña temporal nueva.</li>
          </ul>
        </Modal>
      )}

      {editando && data && (
        <EditarModal
          colaborador={data.colaboradores.find((c) => c.user_id === editando)}
          catalogo={catalogo}
          doctores={doctores}
          doctoresError={doctoresError}
          onClose={() => setEditando(null)}
          onCambio={cargar}
        />
      )}
    </div>
  );
}

/** Una fila: el correo, lo que REALMENTE puede hacer y hasta dónde llega. */
function Fila({ c, esYo, nombreCap, nombreDoctor, doctoresError, onRevocar, onRegenerar, onEditar }) {
  const temporal = estadoTemporal(c);
  return (
    <div className={`rounded-2xl border p-4 ${c.revocado ? "border-slate-200 bg-slate-50 opacity-70" : "border-slate-200 bg-white"}`} data-testid="colab-fila">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0">
          <p className="font-semibold text-slate-900">
            {c.nombre}
            {esYo && <span className="ml-2 text-xs font-normal text-slate-500">(tú)</span>}
          </p>
          <p className="text-sm text-slate-600 break-all">{c.email}</p>

          {/* 🔴 Capacidades REALES, con el nombre del catálogo del backend. Nunca un rol. */}
          <div className="flex flex-wrap gap-1.5 mt-2">
            {c.capacidades.length === 0 ? (
              <span className="text-xs text-slate-500 italic">
                Sin ningún permiso: puede entrar, y no ve nada.
              </span>
            ) : (
              c.capacidades.map((code) => (
                <span key={code} className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700">
                  {nombreCap[code] || code}
                </span>
              ))
            )}
          </div>

          {/* 🔴 El alcance SIEMPRE con palabras. Nunca una lista vacía, que es lo que se
              lee al revés: sin filas significa TODA la clínica, no "nada". */}
          <p className="text-sm mt-2 flex items-start gap-1.5">
            {c.alcance_total ? (
              <>
                <Building2 className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                <span className="text-slate-700">
                  Ve <strong>toda la clínica</strong>: la agenda, los cobros y los gastos de
                  todos los médicos.
                </span>
              </>
            ) : (
              <>
                <Stethoscope className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
                <span className="text-slate-700">
                  Ve <strong>sólo</strong> lo de{" "}
                  {c.alcance_doctores
                    .map((id) => nombreDoctor[id] || (doctoresError ? `médico ${id}` : id))
                    .join(", ")}
                  . El resto de la clínica no le aparece.
                </span>
              </>
            )}
          </p>

          {/* Dos estados distintos, pintados distinto: uno espera al colaborador, el
              otro espera al admin. */}
          {temporal === "pendiente" && (
            <p className="text-sm mt-2 inline-flex items-center gap-1.5 text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1" data-testid="marca-temporal-pendiente">
              <Clock className="w-4 h-4" />
              Contraseña temporal sin usar — caduca el {fechaLarga(c.password_temporal_exp)}
            </p>
          )}
          {temporal === "caducada" && (
            <p className="text-sm mt-2 inline-flex items-center gap-1.5 text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-2 py-1" data-testid="marca-temporal-caducada">
              <ShieldAlert className="w-4 h-4" />
              Contraseña temporal <strong>caducada</strong> — no puede entrar hasta que le
              generes otra
            </p>
          )}
          {c.revocado && (
            <p className="text-sm mt-2 inline-flex items-center gap-1.5 text-slate-600">
              <Ban className="w-4 h-4" /> Acceso revocado
            </p>
          )}
        </div>

        {/* 🔴 Nadie edita sus propios permisos, así que sobre la propia fila la pantalla
            NO OFRECE los controles -- no los ofrece y luego falla con un 409. */}
        {!c.revocado && (
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            {esYo ? (
              <p className="text-xs text-slate-500 max-w-[16rem]">
                Nadie cambia sus propios permisos, ni tú. Pídeselo a otra persona con
                acceso a Colaboradores.
              </p>
            ) : (
              <>
                <button onClick={onEditar} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold" data-testid="btn-editar-colab">
                  Permisos y alcance
                </button>
                <button onClick={onRegenerar} className="px-3 py-1.5 rounded-lg border border-slate-300 text-sm font-semibold inline-flex items-center gap-1.5">
                  <KeyRound className="w-4 h-4" /> Nueva temporal
                </button>
                <button onClick={onRevocar} className="px-3 py-1.5 rounded-lg border border-rose-300 text-rose-700 text-sm font-semibold">
                  Revocar
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Alta: los tres atajos, las 9 casillas siempre visibles, y el alcance aparte. */
function AltaModal({ catalogo, doctores, doctoresError, onClose, onCreado }) {
  const [nombre, setNombre] = useState("");
  const [email, setEmail] = useState("");
  const [telefono, setTelefono] = useState("");
  const [caps, setCaps] = useState([]);
  // `atajo` vive SÓLO en la memoria de esta pantalla mientras está abierta: no se
  // guarda, no viaja en el cuerpo de la petición y desaparece al cerrar el modal. Lo
  // único que hace es recordar que se pulsó "Médico" para exigir la vinculación.
  const [atajo, setAtajo] = useState(null);
  const [alcanceTotal, setAlcanceTotal] = useState(true);
  const [alcance, setAlcance] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState(null);

  const exigeMedico = ATAJOS.find((a) => a.id === atajo)?.exigeMedico;
  const faltaVinculo = exigeMedico && (alcanceTotal || alcance.length === 0);
  const puedeGuardar = nombre.trim() && email.trim() && !faltaVinculo && !enviando;

  const aplicarAtajo = (a) => {
    setAtajo(a.id);
    setCaps(a.caps);
    if (a.exigeMedico) setAlcanceTotal(false);
  };

  const toggleCap = (code) =>
    setCaps((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));

  const toggleDoctor = (id) =>
    setAlcance((prev) => (prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id]));

  const guardar = async () => {
    setEnviando(true);
    setError(null);
    try {
      const { data: r } = await api.post("/colaboradores", {
        nombre: nombre.trim(),
        email: email.trim(),
        telefono: telefono.trim(),
        capacidades: caps,
        // 🔴 Alcance vacío = TODA la clínica. Es lo que significa la ausencia de filas
        //    (COL.8), y por eso la pantalla obliga a elegir una de las dos opciones con
        //    su nombre, en vez de dejar que el vacío decida por descuido.
        alcance: alcanceTotal ? [] : alcance,
      });
      onCreado({
        email: r.email, password: r.password_temporal,
        exp: r.password_temporal_exp, aviso: r.aviso,
      });
    } catch (e) {
      // 🔴 El texto sale del backend tal cual, incluido el del 409 del tope de plan.
      //    Duplicarlo aquí sería el caso `DOCTOR_PLAN_LIMITS`: dos textos que se
      //    desincronizan y uno de los dos acaba mintiendo.
      setError(mensajeDe(e));
      setEnviando(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Dar de alta a un colaborador"
      size="lg"
      testid="modal-alta-colaborador"
      footer={
        <>
          <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold">
            Cancelar
          </button>
          <button onClick={guardar} disabled={!puedeGuardar} className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40" data-testid="btn-guardar-colaborador">
            {enviando ? "Creando…" : "Crear y generar contraseña"}
          </button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <Campo label="Nombre" value={nombre} onChange={setNombre} />
          <Campo label="Correo" value={email} onChange={setEmail} type="email" />
          <Campo label="Teléfono (opcional)" value={telefono} onChange={setTelefono} />
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900">Atajos</p>
          <p className="text-xs text-slate-500 mt-0.5">
            Marcan casillas y nada más. No se guardan en ninguna parte, y después puedes
            cambiar lo que quieras: lo que cuenta son las casillas de abajo.
          </p>
          <div className="flex flex-wrap gap-2 mt-2">
            {ATAJOS.map((a) => (
              <button
                key={a.id}
                type="button"
                onClick={() => aplicarAtajo(a)}
                title={a.pista}
                className={`px-3 py-1.5 rounded-xl border text-sm font-semibold ${
                  atajo === a.id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-300"
                }`}
                data-testid={`atajo-${a.id}`}
              >
                {a.etiqueta}
              </button>
            ))}
          </div>
        </div>

        {/* 🔴 Las 9 casillas SIEMPRE visibles, también después de pulsar un atajo: si
            desaparecieran, el atajo dejaría de ser un atajo y pasaría a ser un rol. */}
        <div>
          <p className="text-sm font-semibold text-slate-900">Qué podrá hacer</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {catalogo.map((cap) => (
              <label key={cap.code} className="flex items-start gap-2 p-2 rounded-xl border border-slate-200 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={caps.includes(cap.code)}
                  onChange={() => toggleCap(cap.code)}
                  data-testid={`cap-${cap.code}`}
                />
                <span>
                  <span className="text-sm font-medium text-slate-900">{cap.nombre}</span>
                  {cap.descripcion && (
                    <span className="block text-xs text-slate-500">{cap.descripcion}</span>
                  )}
                </span>
              </label>
            ))}
          </div>
          {caps.length === 0 && (
            <p className="text-xs text-slate-500 mt-2">
              Sin ninguna casilla marcada podrá entrar y no verá nada. Es válido, pero rara
              vez es lo que se quiere.
            </p>
          )}
        </div>

        <SelectorAlcance
          alcanceTotal={alcanceTotal}
          setAlcanceTotal={setAlcanceTotal}
          alcance={alcance}
          toggleDoctor={toggleDoctor}
          doctores={doctores}
          doctoresError={doctoresError}
        />

        {faltaVinculo && (
          <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="aviso-falta-vinculo">
            <p className="font-semibold flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Falta vincularlo a un médico
            </p>
            <p className="mt-1">
              Elegiste el atajo de médico. Sin vincularlo, su alcance queda vacío — y un
              alcance vacío significa <strong>toda la clínica</strong>: vería la agenda y
              los cobros de todos los demás. Elige al menos un médico arriba.
            </p>
          </div>
        )}

        {error && (
          <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900" data-testid="error-alta">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function SelectorAlcance({ alcanceTotal, setAlcanceTotal, alcance, toggleDoctor, doctores, doctoresError }) {
  return (
    <div>
      <p className="text-sm font-semibold text-slate-900">Hasta dónde ve</p>
      {/* 🔴 Las dos opciones se NOMBRAN. Ninguna es un default silencioso: la ausencia de
          alcance significa "toda la clínica", y eso es contraintuitivo -- si la pantalla
          no lo dice con palabras, alguien lo va a leer al revés. */}
      <div className="space-y-2 mt-2">
        <label className="flex items-start gap-2 p-3 rounded-xl border border-slate-200 cursor-pointer">
          <input type="radio" className="mt-1" checked={alcanceTotal}
                 onChange={() => setAlcanceTotal(true)} data-testid="alcance-total" />
          <span>
            <span className="text-sm font-medium text-slate-900">Toda la clínica</span>
            <span className="block text-xs text-slate-500">
              Ve la agenda, los cobros y los gastos de todos los médicos.
            </span>
          </span>
        </label>
        <label className="flex items-start gap-2 p-3 rounded-xl border border-slate-200 cursor-pointer">
          <input type="radio" className="mt-1" checked={!alcanceTotal}
                 onChange={() => setAlcanceTotal(false)} data-testid="alcance-acotado" />
          <span>
            <span className="text-sm font-medium text-slate-900">Sólo algunos médicos</span>
            <span className="block text-xs text-slate-500">
              El resto de la clínica no le aparece en agenda, cobros ni gastos.
            </span>
          </span>
        </label>
      </div>

      {!alcanceTotal && (
        <div className="mt-3">
          {doctoresError ? (
            <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
              No se pudo cargar el catálogo de médicos: {doctoresError} Sin él no se puede
              acotar el alcance desde aquí.
            </div>
          ) : doctores === null ? (
            <p className="text-sm text-slate-500">Cargando médicos…</p>
          ) : doctores.length === 0 ? (
            <p className="text-sm text-slate-600">
              Esta clínica no tiene médicos dados de alta todavía.
            </p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-2">
              {doctores.map((d) => (
                <label key={d.id} className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 cursor-pointer">
                  <input type="checkbox" checked={alcance.includes(d.id)}
                         onChange={() => toggleDoctor(d.id)} data-testid={`doctor-${d.id}`} />
                  <span className="text-sm text-slate-800">{d.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** 🔴 La contraseña temporal se ve UNA sola vez. El modal no se cierra sin que el admin
 *  confirme que la copió: si se pierde, no se recupera -- se regenera. */
function TemporalModal({ datos, onClose }) {
  const [copiada, setCopiada] = useState(false);
  const [confirmada, setConfirmada] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(datos.password);
      setCopiada(true);
    } catch {
      // Sin portapapeles (navegador o permiso), la contraseña sigue visible en pantalla:
      // se puede seleccionar a mano. No se bloquea el flujo por esto.
      toast.message("Cópiala a mano: el navegador no dejó usar el portapapeles.");
      setCopiada(true);
    }
  };

  return (
    <Modal
      open
      onClose={confirmada ? onClose : undefined}
      title="Contraseña temporal"
      testid="modal-temporal"
      footer={
        <button onClick={onClose} disabled={!confirmada}
                className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-semibold disabled:opacity-40"
                data-testid="btn-cerrar-temporal">
          Listo
        </button>
      }
    >
      <p className="text-sm text-slate-700">
        Para <strong>{datos.email}</strong>. Entrégasela por un medio que controles; al
        entrar, el sistema le va a pedir que la cambie.
      </p>

      <div className="mt-3 flex items-center gap-2">
        <code className="flex-1 px-3 py-2 rounded-xl bg-slate-900 text-white font-mono text-lg tracking-wider select-all" data-testid="password-temporal">
          {datos.password}
        </code>
        <button onClick={copiar} className="px-3 py-2 rounded-xl border border-slate-300 inline-flex items-center gap-1.5 text-sm font-semibold" data-testid="btn-copiar-temporal">
          {copiada ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
          {copiada ? "Copiada" : "Copiar"}
        </button>
      </div>

      <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <p className="font-semibold">Esta contraseña no se puede volver a ver.</p>
        <p className="mt-1">
          {datos.aviso} Caduca el <strong>{fechaLarga(datos.exp)}</strong>. Si se pierde o
          caduca, se genera otra desde la lista — no hay forma de recuperarla.
        </p>
      </div>

      <label className="flex items-start gap-2 mt-4 cursor-pointer">
        <input type="checkbox" className="mt-1" checked={confirmada}
               onChange={(e) => setConfirmada(e.target.checked)}
               data-testid="check-confirmar-copia" />
        <span className="text-sm text-slate-800">
          Ya la copié y la tengo guardada en un sitio seguro.
        </span>
      </label>
    </Modal>
  );
}

/** Gestión: capacidades y alcance de alguien que ya existe. Una operación por clic,
 *  contra el endpoint que corresponde -- no hay "guardar todo" porque el backend no
 *  tiene una operación así, y fingirla escondería a medias qué falló. */
function EditarModal({ colaborador: c, catalogo, doctores, doctoresError, onClose, onCambio }) {
  const [ocupado, setOcupado] = useState(null);
  const [aviso, setAviso] = useState(null);
  const [error, setError] = useState(null);

  if (!c) return null;

  const correr = async (clave, fn) => {
    setOcupado(clave);
    setError(null);
    try {
      const r = await fn();
      // ⚠️ Los dos avisos que el backend manda porque son justo lo que se lee al revés.
      if (r?.data?.paso_de_clinica_completa_a_acotado)
        setAviso("Desde ahora sólo ve lo de los médicos que le asignaste. Antes veía toda la clínica.");
      else if (r?.data?.ahora_ve_toda_la_clinica)
        setAviso("Le quitaste el último médico, así que vuelve a ver TODA la clínica — no se quedó sin nada.");
      else setAviso(null);
      await onCambio();
    } catch (e) {
      setError(mensajeDe(e));
    } finally {
      setOcupado(null);
    }
  };

  const tiene = (code) => c.capacidades.includes(code);

  return (
    <Modal open onClose={onClose} title={`Permisos de ${c.nombre}`} size="lg" testid="modal-editar-colab"
           footer={<button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-300 text-sm font-semibold">Cerrar</button>}>
      <div className="space-y-5">
        <div>
          <p className="text-sm font-semibold text-slate-900">Qué puede hacer</p>
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            {catalogo.map((cap) => (
              <label key={cap.code} className="flex items-start gap-2 p-2 rounded-xl border border-slate-200 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={tiene(cap.code)}
                  disabled={ocupado === cap.code}
                  onChange={() =>
                    correr(cap.code, () =>
                      tiene(cap.code)
                        ? api.post(`/colaboradores/${c.user_id}/capacidades/${cap.code}/revocar`)
                        : api.post(`/colaboradores/${c.user_id}/capacidades`, { capacidad: cap.code }))
                  }
                  data-testid={`edit-cap-${cap.code}`}
                />
                <span>
                  <span className="text-sm font-medium text-slate-900">{cap.nombre}</span>
                  {cap.descripcion && <span className="block text-xs text-slate-500">{cap.descripcion}</span>}
                </span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <p className="text-sm font-semibold text-slate-900">Hasta dónde ve</p>
          <p className="text-sm text-slate-700 mt-1">
            {c.alcance_total
              ? "Ahora mismo ve toda la clínica. En cuanto le asignes un médico, dejará de ver el resto."
              : "Ahora mismo sólo ve lo de los médicos marcados. Si los quitas todos, vuelve a ver toda la clínica."}
          </p>
          <div className="mt-2">
            {doctoresError ? (
              <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900">
                No se pudo cargar el catálogo de médicos: {doctoresError}
              </div>
            ) : doctores === null ? (
              <p className="text-sm text-slate-500">Cargando médicos…</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-2">
                {doctores.map((d) => {
                  const marcado = c.alcance_doctores.includes(d.id);
                  return (
                    <label key={d.id} className="flex items-center gap-2 p-2 rounded-xl border border-slate-200 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={marcado}
                        disabled={ocupado === d.id}
                        onChange={() =>
                          correr(d.id, () =>
                            marcado
                              ? api.post(`/colaboradores/${c.user_id}/alcance/${d.id}/revocar`)
                              : api.post(`/colaboradores/${c.user_id}/alcance`, { doctor_id: d.id }))
                        }
                        data-testid={`edit-doctor-${d.id}`}
                      />
                      <span className="text-sm text-slate-800">{d.name}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {aviso && (
          <div className="rounded-xl border border-sky-300 bg-sky-50 p-3 text-sm text-sky-900" data-testid="aviso-alcance">
            {aviso}
          </div>
        )}
        {error && (
          <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-900" data-testid="error-editar">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Campo({ label, value, onChange, type = "text" }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-xl border border-slate-300 text-sm"
      />
    </label>
  );
}
